import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

export const STATUS_BADGE_CLASSES = {
  draft: "bg-slate-200 text-slate-700 hover:bg-slate-200",
  sent: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  paid: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  overdue: "bg-red-100 text-red-800 hover:bg-red-100",
  cancelled: "bg-slate-100 text-slate-500 hover:bg-slate-100",
};

const STATUS_OPTIONS = ["", "draft", "sent", "overdue", "paid", "cancelled"];

export default function Invoices() {
  const { t, i18n } = useTranslation();
  const { company } = useAuth();
  const navigate = useNavigate();
  const language = i18n.language?.split("-")[0] || "en";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter) params.set("status", statusFilter);
      params.set("page", "1");
      params.set("page_size", "100");
      const { data } = await api.get(`/invoices?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err, t("invoices.toasts.error")));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, t]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  const filteredCount = useMemo(() => total, [total]);

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <h1 className="font-heading text-4xl tracking-tight text-slate-900">
            {t("invoices.title")}
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-slate-500">
            {t("invoices.subtitle", { company: company?.name || "—" })}
          </p>
        </div>
        <Button
          onClick={() => navigate("/app/invoices/new")}
          data-testid="invoices-new-button"
          className="gap-2 bg-slate-900 text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          {t("invoices.new")}
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t("invoices.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ps-9"
            data-testid="invoices-search"
          />
        </div>
        <div className="min-w-[180px]">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="invoices-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s || "all"} value={s || "all"}>
                  {s ? t(`invoices.status.${s}`) : t("invoices.allStatuses")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ms-auto text-xs text-slate-500" data-testid="invoices-total">
          {t("common.totalCount", { count: filteredCount })}
        </div>
      </div>

      <div className="mobile-cards overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("invoices.table.number")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("invoices.table.customer")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("invoices.table.issued")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("invoices.table.due")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("invoices.table.status")}
              </TableHead>
              <TableHead className="text-end text-xs uppercase tracking-wide text-slate-500">
                {t("invoices.table.total")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">
                  {t("invoices.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/app/invoices/${inv.id}`)}
                  data-testid={`invoice-row-${inv.invoice_number}`}
                >
                  <TableCell data-label={t("invoices.table.number")}>
                    <Link
                      to={`/app/invoices/${inv.id}`}
                      className="font-mono text-sm font-medium text-slate-900 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {inv.invoice_number}
                    </Link>
                    {inv.type === "credit" && (
                      <Badge className="ms-2 bg-amber-100 text-amber-800 hover:bg-amber-100">
                        {t("invoices.type.credit")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-700" data-label={t("invoices.table.customer")}>
                    {inv.customer.name}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm" data-label={t("invoices.table.issued")}>
                    {formatDate(inv.issue_date, language)}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm" data-label={t("invoices.table.due")}>
                    {formatDate(inv.due_date, language)}
                  </TableCell>
                  <TableCell data-label={t("invoices.table.status")}>
                    <Badge
                      className={STATUS_BADGE_CLASSES[inv.status] || STATUS_BADGE_CLASSES.draft}
                      data-testid={`invoice-status-${inv.invoice_number}`}
                    >
                      {t(`invoices.status.${inv.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="text-end font-medium tabular-nums text-slate-900"
                    data-label={t("invoices.table.total")}
                  >
                    {formatCurrency(inv.total, inv.currency, language)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
